import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useApp } from "../../../../context/AppState";
import { parseCssStringToObject } from "../../../../helper";
import { DEFAULT_BLOCK_CSS } from "../../../../constant/constant";
import {
  getQuantityDisplay,
  QUANTITY_LAYOUT,
} from "../../../../../shared/product-block-display.js";
import { resolvePopupThemeBorderRadius } from "../../../../utils/resolve-popup-theme-border-radius.js";
import { resolvePopupThemeTokens } from "../../../../utils/resolve-popup-theme-tokens.js";

const BORDER_STYLE_KEYS = [
  "border",
  "borderWidth",
  "borderStyle",
  "borderColor",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomLeftRadius",
  "borderBottomRightRadius",
];

function withoutBorderStyles(style = {}) {
  const next = { ...style };
  for (const key of BORDER_STYLE_KEYS) {
    delete next[key];
  }
  return next;
}

function pickThemeBorderStyle(style = {}) {
  const width = style.borderWidth ?? style.borderTopWidth;
  const color = style.borderColor ?? style.borderTopColor ?? "#d1d5db";
  const borderStyle = style.borderStyle ?? style.borderTopStyle ?? "solid";
  if (width == null || width === "" || width === 0 || width === "0") {
    return null;
  }
  return {
    borderWidth: width,
    borderStyle,
    borderColor: color,
  };
}

function InlineSpinner() {
  return (
    <span
      className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"
      aria-label="Loading"
      role="status"
    />
  );
}

function StepperControls({
  blockCSS,
  buttonStyle,
  hoverStyle,
  name,
  control,
  isCustomCart,
  productData,
  index,
  loadingLines,
  section,
  handleDecrement,
  handleIncrement,
  compact = false,
  premium = false,
  stretchFullWidth = false,
}) {
  const btnPad = premium
    ? "0"
    : stretchFullWidth
      ? "10px 12px"
      : compact
        ? "6px 10px"
        : "8px 12px";
  const stretchItemStyle = stretchFullWidth || premium
    ? {
        flex: "1 1 0",
        minWidth: 0,
        height: "auto",
        minHeight: premium ? 38 : 40,
        maxHeight: premium ? 38 : 44,
      }
    : {};
  const premiumBtnStyle = premium
    ? {
        fontSize: 16,
        fontWeight: 400,
        color: "#334155",
        lineHeight: 1,
      }
    : {};
  const premiumInputStyle = premium
    ? {
        fontWeight: 600,
        fontSize: 13,
        color: "#0f172a",
      }
    : {};
  // Merchant Design → Quantity size/color must win over premium defaults.
  const merchantType = {
    ...(blockCSS?.fontSize ? { fontSize: blockCSS.fontSize } : {}),
    ...(blockCSS?.color ? { color: blockCSS.color } : {}),
  };
  const idleBtnColor = merchantType.color || (premium ? "#334155" : "");
  const premiumHover = premium
    ? {
        backgroundColor: "#f5f2ee",
        color: merchantType.color || "#0f172a",
      }
    : hoverStyle;
  return (
    <>
      <button
        type="button"
        onClick={handleDecrement}
        style={{
          ...blockCSS,
          ...buttonStyle,
          ...stretchItemStyle,
          ...premiumBtnStyle,
          ...merchantType,
          padding: btnPad,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
        onMouseEnter={(e) => {
          e.target.style.background = premiumHover.backgroundColor;
          e.target.style.color = premiumHover.color;
        }}
        onMouseLeave={(e) => {
          e.target.style.background = premium
            ? blockCSS?.backgroundColor || "#ffffff"
            : buttonStyle.background;
          e.target.style.color = idleBtnColor;
        }}
        disabled={loadingLines?.includes(`${index + 1}_decrement`)}
      >
        {isCustomCart && loadingLines?.includes(`${index + 1}_decrement`) ? (
          <InlineSpinner />
        ) : (
          "–"
        )}
      </button>
      <Controller
        name={name}
        control={control}
        defaultValue={1}
        render={({ field }) => (
          <input
            {...field}
            value={isCustomCart ? productData?.quantity : field?.value || 1}
            disabled
            readOnly
            style={{
              ...blockCSS,
              ...stretchItemStyle,
              ...premiumInputStyle,
              ...merchantType,
              background: premium
                ? blockCSS?.backgroundColor || "#ffffff"
                : "transparent",
              backgroundColor: premium
                ? blockCSS?.backgroundColor || "#ffffff"
                : "transparent",
              borderWidth: 0,
              borderRadius: 0,
              textAlign: "center",
              pointerEvents: "none",
              minWidth: stretchFullWidth || premium ? 0 : compact ? 32 : 40,
              padding: premium
                ? "0 4px"
                : stretchFullWidth
                  ? "10px 4px"
                  : compact
                    ? "6px 4px"
                    : "8px 4px",
            }}
          />
        )}
      />
      <button
        type="button"
        onClick={handleIncrement}
        style={{
          ...blockCSS,
          ...buttonStyle,
          ...stretchItemStyle,
          ...premiumBtnStyle,
          ...merchantType,
          padding: btnPad,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }}
        onMouseEnter={(e) => {
          e.target.style.background = premiumHover.backgroundColor;
          e.target.style.color = premiumHover.color;
        }}
        onMouseLeave={(e) => {
          e.target.style.background = premium
            ? blockCSS?.backgroundColor || "#ffffff"
            : buttonStyle.background;
          e.target.style.color = idleBtnColor;
        }}
        disabled={loadingLines?.includes(`${index + 1}_increment`)}
      >
        {isCustomCart && loadingLines?.includes(`${index + 1}_increment`) ? (
          <InlineSpinner />
        ) : (
          "+"
        )}
      </button>
    </>
  );
}

const QuantityPickerBlock = ({
  section,
  index,
  productIndex,
  productData,
  isCustomCart,
  updateQuantity,
  loadingLines,
  popupData,
  activeStep = 0,
  stretchFullWidth = false,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const { isMobile } = useApp();
  const display = getQuantityDisplay(section);
  const layout = display.layout || QUANTITY_LAYOUT.LABELED_ROW;

  const name = `${section?.type}-${productIndex}`;

  const rawBlockCSS = {
    ...DEFAULT_BLOCK_CSS,
    flexWrap: "nowrap",
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
  };
  const themeBorder = pickThemeBorderStyle(rawBlockCSS);
  const blockCSS = withoutBorderStyles({
    ...rawBlockCSS,
    ...(stretchFullWidth
      ? {
          width: "100%",
          height: "auto",
          minHeight: "unset",
          maxHeight: "none",
        }
      : {}),
  });
  const shellBorderRadiusPx = resolvePopupThemeBorderRadius(popupData, {
    activeStep,
    isMobile,
  });
  const themeTokens = resolvePopupThemeTokens(popupData, { activeStep, isMobile });

  const isPremiumCompact = layout === QUANTITY_LAYOUT.PREMIUM_COMPACT;
  const merchantRadius =
    rawBlockCSS.borderRadius != null && rawBlockCSS.borderRadius !== ""
      ? typeof rawBlockCSS.borderRadius === "number"
        ? `${rawBlockCSS.borderRadius}px`
        : String(rawBlockCSS.borderRadius)
      : `${shellBorderRadiusPx}px`;
  const merchantBg = rawBlockCSS.backgroundColor;

  const premiumShellStyle = {
    display: "flex",
    alignItems: "stretch",
    width: "100%",
    border: themeBorder ? undefined : "1px solid #d4c4b0",
    ...(themeBorder || {}),
    borderRadius: merchantRadius,
    overflow: "hidden",
    background: merchantBg || "#ffffff",
    backgroundColor: merchantBg || "#ffffff",
    minHeight: isMobile ? 36 : 38,
  };

  const premiumBlockCSS = withoutBorderStyles({
    ...rawBlockCSS,
    width: "100%",
    height: "auto",
    minHeight: "unset",
    maxHeight: "none",
    backgroundColor: merchantBg || "#ffffff",
    background: merchantBg || "#ffffff",
  });

  const buttonStyle = isPremiumCompact
    ? {
        background: merchantBg || "#ffffff",
        backgroundColor: merchantBg || "#ffffff",
        borderWidth: 0,
        cursor: "pointer",
        transition: "background-color 0.15s ease",
      }
    : {
        background: "transparent",
        borderWidth: 0,
        cursor: "pointer",
        transition: "all 100ms ease",
      };
  const hoverStyle = isPremiumCompact
    ? {
        backgroundColor: "#f8fafc",
        color: "#0f172a",
      }
    : {
        backgroundColor: `color-mix(in srgb, ${themeTokens.border} 35%, white)`,
        color: themeTokens.text,
      };

  const stepperShellStyle = isPremiumCompact
    ? premiumShellStyle
    : {
        display: stretchFullWidth ? "flex" : "inline-flex",
        alignItems: "stretch",
        width: stretchFullWidth ? "100%" : undefined,
        border: themeBorder ? undefined : "none",
        ...(themeBorder || {}),
        borderRadius: merchantRadius,
        overflow: "hidden",
        background: merchantBg || "transparent",
        backgroundColor: merchantBg || "transparent",
      };

  const handleIncrement = () => {
    if (isCustomCart) {
      updateQuantity(index + 1, productData?.quantity + 1, "increment");
    }
    const currentValue = getValues(name);
    if (section?.max) {
      if (currentValue < section?.max) setValue(name, currentValue + 1);
    } else {
      setValue(name, currentValue + 1);
    }
  };

  const handleDecrement = () => {
    if (isCustomCart) {
      updateQuantity(index + 1, productData?.quantity - 1, "decrement");
    }
    const currentValue = getValues(name);
    if (section?.min) {
      if (currentValue > section?.min) setValue(name, currentValue - 1);
    } else if (currentValue > 1) {
      setValue(name, currentValue - 1);
    }
  };

  const stepper = (
    <div style={stepperShellStyle}>
      <StepperControls
        blockCSS={isPremiumCompact ? premiumBlockCSS : blockCSS}
        buttonStyle={buttonStyle}
        hoverStyle={hoverStyle}
        name={name}
        control={control}
        isCustomCart={isCustomCart}
        productData={productData}
        index={index}
        loadingLines={loadingLines}
        section={section}
        handleDecrement={handleDecrement}
        handleIncrement={handleIncrement}
        compact={layout === QUANTITY_LAYOUT.COMPACT}
        premium={isPremiumCompact}
        stretchFullWidth={stretchFullWidth || isPremiumCompact}
      />
    </div>
  );

  if (layout === QUANTITY_LAYOUT.LABELED_ROW) {
    const showBadge =
      display.showIncentiveBadge === true && Boolean(display.incentiveText);
    return (
      <div
        style={{
          ...blockCSS,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
          border: "none",
          background: "transparent",
        }}
      >
        {display.label && layout !== QUANTITY_LAYOUT.COMPACT ? (
          <span
            style={{
              fontSize: isMobile ? 13 : 14,
              fontWeight: 700,
              color: themeTokens.heading,
            }}
          >
            {display.label}
          </span>
        ) : null}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          {stepper}
          {showBadge ? (
            <span
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                background: "#ecfdf5",
                color: "#065f46",
                fontSize: isMobile ? 11 : 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {display.incentiveText}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  if (layout === QUANTITY_LAYOUT.PREMIUM_COMPACT || layout === QUANTITY_LAYOUT.COMPACT) {
    return (
      <div
        style={{
          ...(isPremiumCompact ? premiumBlockCSS : blockCSS),
          display: "flex",
          justifyContent: stretchFullWidth || isPremiumCompact ? "stretch" : "center",
          width: "100%",
          border: "none",
          background: "transparent",
        }}
      >
        {stepper}
      </div>
    );
  }

  return (
    <div
      style={{
        ...blockCSS,
        display: "flex",
        width: "100%",
        border: "none",
      }}
    >
      <div style={{ ...stepperShellStyle, width: "100%" }}>
        <StepperControls
          blockCSS={{ ...blockCSS, flex: 1 }}
          buttonStyle={buttonStyle}
          hoverStyle={hoverStyle}
          name={name}
          control={control}
          isCustomCart={isCustomCart}
          productData={productData}
          index={index}
          loadingLines={loadingLines}
          section={section}
          handleDecrement={handleDecrement}
          handleIncrement={handleIncrement}
          stretchFullWidth={stretchFullWidth}
        />
      </div>
    </div>
  );
};

export default QuantityPickerBlock;
